import { MultiSelectBadges } from '@/components/admin/MultiSelectBadges';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useEffect, useState } from 'react';

// Re-define the RestrictionConfig type locally to avoid import issues
interface RestrictionConfig {
  allowed_ehr_ids?: string[];
  blocked_ehr_ids?: string[];
  allowed_duration_categories?: ('short' | 'medium' | 'long' | 'other')[];
  allowed_condition_tags?: string[];
  blocked_all?: boolean;
  no_urgency_override?: boolean; // If true, urgency window does NOT bypass allowed_* restrictions
  patient_status?: 'new' | 'existing' | 'any';
  reason?: string;
}

interface EditRestrictionConfigDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (restrictionConfig: RestrictionConfig) => void;
  labelPattern: string;
  currentConfig?: RestrictionConfig | null;
  appointmentTypes: Array<{
    ehrId: string;
    name: string;
    abbreviation: string;
    durationCategory: string;
  }>;
  conditionTags?: Array<{
    tagKey: string;
    label: string;
    description: string;
  }>;
  isLoading?: boolean;
}

export function EditRestrictionConfigDialog({
  isOpen,
  onClose,
  onSave,
  labelPattern,
  currentConfig,
  appointmentTypes,
  conditionTags = [],
  isLoading = false,
}: EditRestrictionConfigDialogProps) {
  // Form state
  const [blockedAll, setBlockedAll] = useState(false);
  const [noUrgencyOverride, setNoUrgencyOverride] = useState(false);
  const [allowedEhrIds, setAllowedEhrIds] = useState<string[]>([]);
  const [blockedEhrIds, setBlockedEhrIds] = useState<string[]>([]);
  const [allowedDurationCategories, setAllowedDurationCategories] = useState<string[]>([]);
  const [allowedConditionTags, setAllowedConditionTags] = useState<string[]>([]);
  const [patientStatus, setPatientStatus] = useState<'new' | 'existing' | 'any'>('any');
  const [reason, setReason] = useState('');
  const [showBlockAllConfirmation, setShowBlockAllConfirmation] = useState(false);

  // Initialize form with current config when dialog opens
  useEffect(() => {
    if (isOpen) {
      setBlockedAll(currentConfig?.blocked_all || false);
      setNoUrgencyOverride(currentConfig?.no_urgency_override || false);
      setAllowedEhrIds(currentConfig?.allowed_ehr_ids || []);
      setBlockedEhrIds(currentConfig?.blocked_ehr_ids || []);
      setAllowedDurationCategories(currentConfig?.allowed_duration_categories || []);
      setAllowedConditionTags(currentConfig?.allowed_condition_tags || []);
      setPatientStatus(currentConfig?.patient_status || 'any');
      setReason(currentConfig?.reason || '');
      setShowBlockAllConfirmation(false);
    }
  }, [isOpen, currentConfig]);

  const handleSaveClick = () => {
    if (blockedAll) {
      setShowBlockAllConfirmation(true);
    } else {
      executeSave();
    }
  };

  const executeSave = () => {
    const restrictionConfig: RestrictionConfig = {
      blocked_all: blockedAll,
      no_urgency_override: noUrgencyOverride || undefined,
      allowed_ehr_ids: allowedEhrIds.length > 0 ? allowedEhrIds : undefined,
      blocked_ehr_ids: blockedEhrIds.length > 0 ? blockedEhrIds : undefined,
      allowed_duration_categories:
        allowedDurationCategories.length > 0
          ? (allowedDurationCategories as ('short' | 'medium' | 'long' | 'other')[])
          : undefined,
      allowed_condition_tags: allowedConditionTags.length > 0 ? allowedConditionTags : undefined,
      patient_status: patientStatus !== 'any' ? patientStatus : undefined,
      reason: reason.trim() || undefined,
    };
    onSave(restrictionConfig);
    setShowBlockAllConfirmation(false);
  };

  const addAllowedEhrId = (value: string) => {
    if (!allowedEhrIds.includes(value)) {
      setAllowedEhrIds([...allowedEhrIds, value]);
    }
  };

  const removeAllowedEhrId = (value: string) => {
    setAllowedEhrIds(allowedEhrIds.filter((id) => id !== value));
  };

  const addBlockedEhrId = (value: string) => {
    if (!blockedEhrIds.includes(value)) {
      setBlockedEhrIds([...blockedEhrIds, value]);
    }
  };

  const removeBlockedEhrId = (value: string) => {
    setBlockedEhrIds(blockedEhrIds.filter((id) => id !== value));
  };

  const toggleDurationCategory = (category: string) => {
    if (allowedDurationCategories.includes(category)) {
      setAllowedDurationCategories(allowedDurationCategories.filter((c) => c !== category));
    } else {
      setAllowedDurationCategories([...allowedDurationCategories, category]);
    }
  };

  const addConditionTag = (value: string) => {
    if (!allowedConditionTags.includes(value)) {
      setAllowedConditionTags([...allowedConditionTags, value]);
    }
  };

  const removeConditionTag = (value: string) => {
    setAllowedConditionTags(allowedConditionTags.filter((t) => t !== value));
  };

  // Prepare items for MultiSelectBadges
  const appointmentTypeItems = appointmentTypes.map((type) => ({
    value: type.ehrId,
    label: `${type.name} (${type.abbreviation}) - ${type.ehrId}`,
  }));

  const conditionTagItems = conditionTags.map((tag) => ({
    value: tag.tagKey,
    label: tag.label,
  }));

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Restriction Configuration</DialogTitle>
            <DialogDescription>
              Configure appointment restrictions for slot label: <strong>{labelPattern}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Guidance Text */}
            <div className="rounded-md bg-muted p-4 text-sm text-muted-foreground border">
              <p className="font-semibold mb-2">8-Day Urgency Override Rules:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>
                  <strong className="text-destructive">Never overridden:</strong> Block All, Blocked
                  Appointment Types (deny list)
                </li>
                <li>
                  <strong className="text-amber-600">Can be overridden:</strong> Allowed Appointment
                  Types, Duration Categories, Patient Status
                </li>
                <li>
                  <strong className="text-blue-600">Prevent override:</strong> Enable "No Urgency
                  Override" to make Allowed restrictions strict (e.g., emergency-only slots)
                </li>
              </ul>
              <p className="mt-2 text-xs">
                <strong>Tip:</strong> Use "Blocked Appointment Types" for hard "No XYZ" rules.
                "Allowed" restrictions are softer and can be bypassed for urgent bookings unless "No
                Urgency Override" is enabled.
              </p>
            </div>

            {/* Block All Switch */}
            <div className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label className="text-base">Block All Appointments</Label>
                <p className="text-sm text-muted-foreground">
                  When enabled, no appointments can be scheduled in this slot (holidays, reserved
                  slots, etc.)
                </p>
              </div>
              <Switch checked={blockedAll} onCheckedChange={setBlockedAll} />
            </div>

            {/* No Urgency Override Switch */}
            <div className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <Label className="text-base">No Urgency Override</Label>
                <p className="text-sm text-muted-foreground">
                  When enabled, the 8-day urgency window will NOT bypass "Allowed" restrictions. Use
                  for reserved slots (e.g., emergency-only, specialty-only).
                </p>
              </div>
              <Switch checked={noUrgencyOverride} onCheckedChange={setNoUrgencyOverride} />
            </div>

            {/* Allowed EHR IDs */}
            <MultiSelectBadges
              label="Allowed Appointment Types"
              description="Reserve slot for these types. Can be overridden for urgent 7-day bookings. Leave empty to allow all."
              items={appointmentTypeItems}
              selectedValues={allowedEhrIds}
              onAdd={addAllowedEhrId}
              onRemove={removeAllowedEhrId}
              placeholder="Select appointment type to allow"
              variant="secondary"
            />

            {/* Blocked EHR IDs */}
            <MultiSelectBadges
              label="Blocked Appointment Types (Never Overridden)"
              description="Hard 'No XYZ' rules. These types are always blocked, even for urgent 7-day bookings."
              items={appointmentTypeItems}
              selectedValues={blockedEhrIds}
              onAdd={addBlockedEhrId}
              onRemove={removeBlockedEhrId}
              placeholder="Select appointment type to block"
              variant="destructive"
            />

            {/* Duration Categories */}
            <div className="space-y-2">
              <Label>Allowed Duration Categories</Label>
              <p className="text-sm text-muted-foreground">
                Restrict appointments by their duration category. Leave empty to allow all
                durations.
              </p>
              <div className="flex flex-wrap gap-2">
                {(['short', 'medium', 'long', 'other'] as const).map((category) => (
                  <Button
                    key={category}
                    type="button"
                    variant={allowedDurationCategories.includes(category) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => toggleDurationCategory(category)}
                  >
                    {category}
                  </Button>
                ))}
              </div>
            </div>

            {/* Condition Tags - Specialty Filtering */}
            {(conditionTags.length > 0 || allowedConditionTags.length > 0) && (
              <MultiSelectBadges
                label="Allowed Condition Tags (Specialty Filtering)"
                description='Restrict to specific medical conditions/specialties. Leave empty to allow all conditions. Used for specialty slots like "Any Retina Appointment".'
                items={conditionTagItems}
                selectedValues={allowedConditionTags}
                onAdd={addConditionTag}
                onRemove={removeConditionTag}
                placeholder="Select condition tag to allow"
                variant="secondary"
              />
            )}

            {/* Patient Status */}
            <div className="space-y-2">
              <Label>Patient Status Restriction</Label>
              <p className="text-sm text-muted-foreground">
                Restrict this slot to new patients only, existing patients only, or allow both.
              </p>
              <Select
                value={patientStatus}
                onValueChange={(value) => setPatientStatus(value as 'new' | 'existing' | 'any')}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="any">Any (Both New and Existing Patients)</SelectItem>
                  <SelectItem value="new">New Patients Only</SelectItem>
                  <SelectItem value="existing">Existing Patients Only</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label>Reason</Label>
              <p className="text-sm text-muted-foreground">
                Explain why this restriction configuration is needed (for documentation and
                debugging).
              </p>
              <Textarea
                placeholder="e.g., 'Only allow follow-up appointments during lunch hours'"
                className="min-h-[80px]"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={isLoading}>
              Cancel
            </Button>
            <Button onClick={handleSaveClick} disabled={isLoading}>
              {isLoading ? 'Saving...' : 'Save Configuration'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showBlockAllConfirmation} onOpenChange={setShowBlockAllConfirmation}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm Block All</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to block all? We will not ever be able to schedule in this slot.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={executeSave}>Confirm Block All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
